import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { format, parseISO, isValid } from "date-fns";

import {
  getMimetypeIcon,
  CATEGORY_ICON,
} from "./search-dialog-constants";
import { ResultBreadcrumb } from "./result-breadcrumb";

export const ResultItem = ({ item, showCategory = false, onResultClick }) => {
  const { t } = useTranslation();

  const highlightText =
    item.highlights && Object.keys(item.highlights).length > 0
      ? Object.values(item.highlights).flat()[0]
      : null;

  const modifiedLabel = (() => {
    if (!item.modified) return null;
    const parsed = parseISO(item.modified);
    return isValid(parsed) ? format(parsed, "dd MMMM, yyyy") : null;
  })();

  return (
    <Box
      onClick={() => onResultClick?.(item)}
      sx={{ px: 2, py: 1.5 }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <SvgIcon
          fontSize="small"
          sx={{ mt: 0.25, color: "text.secondary", flexShrink: 0 }}
        >
          <FontAwesomeIcon
            icon={
              getMimetypeIcon(item.content?.mimetype) ??
              CATEGORY_ICON[item.type] ??
              faFile
            }
          />
        </SvgIcon>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                noWrap
                sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              >
                {item.title}
              </Typography>
              {modifiedLabel && (
                <Typography
                  noWrap
                  color="textDisabled"
                  sx={{ fontSize: "0.65rem", fontStyle: "italic", flexShrink: 0 }}
                >
                  · {t("search.updatedOn", { date: modifiedLabel })}
                </Typography>
              )}
            </Box>
            {showCategory && (
              <Chip
                label={item.category ?? t(`search.types.${item.type}`, { defaultValue: item.type })}
                size="small"
                sx={{
                  flexShrink: 0,
                  height: 18,
                  fontSize: "0.65rem",
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            )}
          </Stack>
          <ResultBreadcrumb
            item={item}
            onResultClick={onResultClick}
          />
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              mt: 0.25,
              minHeight: "2.86em",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
            {...(highlightText
              ? { dangerouslySetInnerHTML: { __html: highlightText } }
              : { children: item.description ?? "" })}
          />
        </Box>
      </Stack>
    </Box>
  );
};

ResultItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    url: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    highlights: PropTypes.object,
    modified: PropTypes.string,
  }).isRequired,
  showCategory: PropTypes.bool,
  onResultClick: PropTypes.func,
};